// #region imports

// --- Angular
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

// --- RxJs
import { catchError, map } from 'rxjs/operators';

// --- 3rd Party
import { retryBackoff } from 'backoff-rxjs';
import { TranslateService } from '@ngx-translate/core';

// --- HS Code
import { DemographicsBaseService } from './demographics-base.service';
import { environment } from '../../../../environments/environment';
import { DemographicModel, ApplicationError } from '../../../models';
import { AppService } from '../app.service';

// #endregion

@Injectable({
  providedIn: 'root'
})
export class DemographicsService extends DemographicsBaseService {
  constructor(
    private appService: AppService,
    private httpClient: HttpClient,
    private translate: TranslateService
  ) {
    super();
  }

  private _demographics: DemographicModel[];

  loadDemographics(): void {
    if (this._demographics && this._demographics.length > 0) {
      return;
    }

    const uri =
      environment.apiConfig.baseUrl + environment.apiConfig.demographicsApi;
    this.appService.setLoading(true);
    this._demographicsLoading$.next(true);
    this.httpClient
      .get<DemographicModel[]>(uri)
      .pipe(
        map(response => {
          const mapped = response.map(
            (data: DemographicModel) =>
              new DemographicModel(
                data.id,
                data.name,
                data.isMandatory,
                data.values,
                data.moniker
              )
          );

          return mapped;
        }),
        retryBackoff(environment.retryConfig),
        catchError(this.handleError.bind(this))
      )
      .subscribe((demographics: DemographicModel[]) => {
        this._demographics = demographics as DemographicModel[];
        this._demographics$.next(demographics as DemographicModel[]);
        this._demographicsLoading$.next(false);
        this.appService.setLoading(false);
      });
  }

  private handleError(err) {
    this.appService.setLoading(false);

    let error: ApplicationError;
    if (err && err instanceof HttpErrorResponse) {
      // A client-side or network error occurred. Handle it accordingly.
      let detailedMessage = `${err.message}`;
      if (err.statusText) {
        detailedMessage += `<br/>${err.statusText}`;
      }
      error = {
        errorMessage: this.translate.instant('errorMessageGenericBackend'),
        errorNumber: err.status,
        detailedMessage
      } as ApplicationError;
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      error = {
        errorMessage: this.translate.instant('errorMessageGenericBackend'),
        originalError: err
      } as ApplicationError;
    }
    this.appService.setLastError(error);
  }
}
